import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Paper
} from '@mui/material';
import {
  Add as AddIcon,
  Palette as PaletteIcon,
  Image as ImageIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import {
  getTenantDomains,
  createTenantDomain,
  deleteTenantDomain,
  updateTenantTheme,
  checkSubdomainAvailability
} from '../../services/tenant.service';
import { useTenant } from '../../contexts/TenantContext';
import ApiService from '../../services/api.service';
import { can } from '../../utils/roleUtils';
import {
  adjustColor,
  deriveBrandHover,
  deriveBrandInk,
  deriveBrandSoft,
  deriveStoredCompanion,
  getContrastText,
  isBrandColorPale,
  resolveImageUrl,
  withAlpha,
  DEFAULT_BRAND_COLOR
} from '../../utils/colorUtils';
import { getTenantTitleSx } from '../../utils/titleStyle';

const STYLE_OPTIONS = [
  { key: 'modern', label: 'Modern', description: 'Clean and professional' },
  { key: 'elegant', label: 'Elegant', description: 'Classic serif font' },
  { key: 'bold', label: 'Bold', description: 'Strong and impactful' },
  { key: 'minimal', label: 'Minimal', description: 'Light and airy' },
];

// The Brand Color is chosen from these presets only. Every swatch is light
// enough to work as a sidebar and a filled button without the UI turning heavy,
// with one restrained dark option. Ordering is presentation only — Reset to
// Default reads DEFAULT_BRAND_COLOR, never presets[0].
const BRAND_PRESETS = [
  { color: '#3CAEA3', label: 'Thaklis Teal' },
  { color: DEFAULT_BRAND_COLOR, label: 'Deep Teal' },
  { color: '#0FA3B1', label: 'Aqua' },
  { color: '#3B8FD4', label: 'Sky' },
  { color: '#2F6FE0', label: 'Blue' },
  { color: '#12A05F', label: 'Emerald' },
  { color: '#D9860B', label: 'Amber' },
  { color: '#E2604A', label: 'Coral' },
  { color: '#7C5CD6', label: 'Lavender' },
  { color: '#D64C7F', label: 'Rose' },
  { color: '#3A4450', label: 'Charcoal' },
];

const defaults = {
  brandColor: DEFAULT_BRAND_COLOR,
  titleStyle: 'modern',
};

const PreviewLabel = ({ children }) => (
  <Typography variant="caption" sx={{ mb: 0.75, display: 'block', fontWeight: 600 }}>
    {children}
  </Typography>
);

/**
 * The generated variations, read-only. One colour is configurable; this row
 * exists so the admin can see what the rest of the palette becomes and why the
 * app is not a single flat block of their chosen colour.
 */
const BrandPaletteRow = ({ brand, brandSoft, brandHover, brandInk, brandOn }) => {
  const chips = [
    { swatch: brand, label: 'Brand', sub: 'Your colour', text: brandOn },
    { swatch: brandSoft, label: 'Soft', sub: 'Subtle backgrounds', text: 'text.primary', outlined: true },
    { swatch: brandHover, label: 'Hover', sub: 'On press', text: getContrastText(brandHover) },
    { swatch: brandInk, label: 'Ink', sub: 'Text & borders', text: getContrastText(brandInk) },
    { swatch: brandOn, label: 'Text', sub: 'On your colour', text: getContrastText(brandOn), outlined: true },
  ];

  return (
    <Box>
      <PreviewLabel>Generated from your Brand Color</PreviewLabel>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {chips.map(({ swatch, label, sub, text, outlined }) => (
          <Box
            key={label}
            sx={{
              flex: '1 1 132px',
              minWidth: 118,
              borderRadius: 1.5,
              px: 1.25,
              py: 1,
              bgcolor: swatch,
              color: text,
              border: outlined ? '1px solid' : '1px solid transparent',
              borderColor: outlined ? 'divider' : 'transparent',
            }}
          >
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, lineHeight: 1.3 }}>{label}</Typography>
            <Typography sx={{ fontSize: '0.62rem', opacity: 0.8, lineHeight: 1.3 }}>{sub}</Typography>
          </Box>
        ))}
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
        You choose one Brand Color. These variations are generated automatically.
      </Typography>
    </Box>
  );
};

const TenantSettings = ({ currentUser, embedded = false }) => {
  const { tenantId, tenantName, theme, refreshConfig } = useTenant();
  const [domains, setDomains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // The tenant configures a single Brand Color. Every other brand token —
  // hover, ink, tint, contrast text — is derived from it, and the legacy
  // secondaryColor field is written as the derived ink shade on save.
  const [themeForm, setThemeForm] = useState({
    brandColor: theme?.primaryColor || defaults.brandColor,
    logoUrl: theme?.logoUrl || '',
    titleStyle: theme?.titleStyle || defaults.titleStyle
  });

  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [newSubdomain, setNewSubdomain] = useState('');
  const [subdomainAvailable, setSubdomainAvailable] = useState(null);
  const [checkingSubdomain, setCheckingSubdomain] = useState(false);

  const typingCenterId = currentUser?.typingCenterId || tenantId;
  const isPlatformAdmin = currentUser?.role === 'PLATFORM_ADMIN';
  const canEdit = can(currentUser, 'settings', 'write');

  // Every derived value comes from the SAME pure helpers the running app uses,
  // fed the unsaved form value — so the preview is what the tenant will get.
  const brand = (themeForm.brandColor || defaults.brandColor).trim();
  const brandInk = deriveBrandInk(brand);
  const brandHover = deriveBrandHover(brand);
  const brandSoft = deriveBrandSoft(brand);
  const brandOn = getContrastText(brand);
  const brandIsPale = isBrandColorPale(brand);
  // Persisted for backward compatibility only; identity is resolved from the
  // Brand Color at runtime, never from this field.
  const companionColor = deriveStoredCompanion(brand);

  useEffect(() => {
    if (typingCenterId) loadDomains();
  }, [typingCenterId]);

  useEffect(() => {
    if (theme) {
      setThemeForm({
        brandColor: theme.primaryColor || defaults.brandColor,
        logoUrl: theme.logoUrl || '',
        titleStyle: theme.titleStyle || defaults.titleStyle
      });
    }
  }, [theme]);

  const loadDomains = async () => {
    try {
      setLoading(true);
      const data = await getTenantDomains(typingCenterId);
      setDomains(data);
    } catch (err) {
      setError('Failed to load domains');
    } finally {
      setLoading(false);
    }
  };

  const handleThemeChange = (field, value) => {
    setThemeForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveTheme = async () => {
    if (!typingCenterId) return;
    try {
      setSaving(true);
      setError(null);
      // Persisted under the existing field names for API/DB compatibility.
      // logoUrl is deliberately not sent — the logo is saved by its own upload
      // flow, so a colour/style save can never disturb it.
      await updateTenantTheme(typingCenterId, {
        primaryColor: themeForm.brandColor,
        secondaryColor: companionColor,
        titleStyle: themeForm.titleStyle
      });
      setSuccess('Branding updated successfully');
      refreshConfig();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save branding');
    } finally {
      setSaving(false);
    }
  };

  // Local reset only — the logo is untouched and nothing is persisted until
  // Save Branding is pressed.
  const handleResetTheme = () => {
    setThemeForm(prev => ({
      ...prev,
      brandColor: defaults.brandColor,
      titleStyle: defaults.titleStyle
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (PNG, JPG, etc.)');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Logo file must be under 2MB');
      return;
    }

    setUploadingLogo(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await ApiService.uploadCompanyLogo(typingCenterId, formData);
      const logoUrl = response.data?.data?.logoUrl || response.data?.logoUrl || response.data?.data?.logo_url;
      if (logoUrl) {
        await updateTenantTheme(typingCenterId, { logoUrl });
        handleThemeChange('logoUrl', logoUrl);
        refreshConfig();
        setSuccess('Logo uploaded successfully');
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload logo');
      console.error('Logo upload error:', err);
    } finally {
      setUploadingLogo(false);
      e.target.value = '';
    }
  };

  const handleCheckSubdomain = async () => {
    if (!newSubdomain || newSubdomain.length < 3) return;
    setCheckingSubdomain(true);
    try {
      const result = await checkSubdomainAvailability(newSubdomain.toLowerCase());
      setSubdomainAvailable(result.available);
    } catch (err) {
      setSubdomainAvailable(null);
    } finally {
      setCheckingSubdomain(false);
    }
  };

  const handleAddDomain = async () => {
    if (!newSubdomain || !subdomainAvailable) return;
    try {
      setSaving(true);
      await createTenantDomain({ typingCenterId, subdomain: newSubdomain.toLowerCase() });
      setOpenAddDialog(false);
      setNewSubdomain('');
      setSubdomainAvailable(null);
      loadDomains();
      setSuccess('Domain added successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add domain');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDomain = async (domainId) => {
    if (!window.confirm('Delete this domain?')) return;
    try {
      await deleteTenantDomain(domainId);
      loadDomains();
      setSuccess('Domain deleted');
    } catch (err) {
      setError('Failed to delete domain');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {!embedded && (
        <>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            {isPlatformAdmin ? 'Tenant Settings' : 'Branding Settings'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {isPlatformAdmin
              ? `Configure subdomain and branding for ${tenantName}`
              : `Customize the look and feel of your portal for ${currentUser?.typingCenterName || tenantName}`
            }
          </Typography>
        </>
      )}

      {!canEdit && <Alert severity="info" sx={{ mb: 2 }}>You have view-only access to these settings.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* Portal URL info for TC_ADMIN */}
      {!isPlatformAdmin && domains.length > 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Your Portal URL:</strong>{' '}
            <a
              href={`https://${domains[0]?.subdomain}.${process.env.REACT_APP_BASE_DOMAIN}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontFamily: 'monospace' }}
            >
              {domains[0]?.subdomain}.{process.env.REACT_APP_BASE_DOMAIN}
            </a>
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Share this URL with your organizations and customers to access the portal with your branding.
          </Typography>
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Subdomain section - only show for Platform Admin */}
        {isPlatformAdmin && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Subdomains</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={() => setOpenAddDialog(true)}>
                    Add Domain
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                {domains.length === 0 ? (
                  <Typography color="text.secondary">No subdomains configured</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {domains.map((d) => (
                      <Chip key={d.id} label={d.domain} color={d.isPrimary ? 'primary' : 'default'}
                        onDelete={domains.length > 1 ? () => handleDeleteDomain(d.id) : undefined}
                        sx={{ fontFamily: 'monospace' }} />
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Branding section */}
        <Grid item xs={12} md={isPlatformAdmin ? 6 : 12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}><PaletteIcon sx={{ mr: 1 }} />Branding</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {/* Brand Color */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Brand Color</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                    Your main identity in light mode. This exact colour fills the sidebar, primary
                    buttons and main table headers. Matching shades and tints for hover, borders and
                    soft backgrounds are generated from it automatically. Dark mode uses a fixed
                    palette and is not affected.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
                    {BRAND_PRESETS.map(({ color, label }) => {
                      const isSelected = themeForm.brandColor?.toLowerCase() === color.toLowerCase();
                      return (
                        <Box
                          key={color}
                          onClick={canEdit ? () => handleThemeChange('brandColor', color) : undefined}
                          sx={{
                            cursor: canEdit ? 'pointer' : 'default',
                            textAlign: 'center',
                            transition: 'transform 0.15s',
                            '&:hover': canEdit ? { transform: 'scale(1.05)' } : undefined,
                          }}
                        >
                          <Box sx={{
                            width: 48, height: 48, borderRadius: '50%',
                            background: `linear-gradient(135deg, ${adjustColor(color, 15)}, ${adjustColor(color, -20)})`,
                            border: isSelected ? '3px solid' : '2px solid',
                            borderColor: isSelected ? 'success.main' : 'transparent',
                            boxShadow: isSelected ? '0 0 0 2px rgba(115,191,105,0.3)' : 'none',
                            mx: 'auto', mb: 0.5,
                          }} />
                          <Typography variant="caption" sx={{ fontSize: '0.65rem', color: 'text.secondary' }}>
                            {label}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>

                {/* Logo */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Logo</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    {themeForm.logoUrl ? (
                      <Box sx={{
                        width: 80, height: 50, borderRadius: 1, border: '1px solid #e0e0e0',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#fafafa'
                      }}>
                        <img src={resolveImageUrl(themeForm.logoUrl)} alt="Logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                      </Box>
                    ) : (
                      <Box sx={{
                        width: 80, height: 50, borderRadius: 1, border: '1px dashed #ccc',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#fafafa'
                      }}>
                        <ImageIcon sx={{ color: '#ccc' }} />
                      </Box>
                    )}
                    {canEdit && (
                      <Button
                        variant="outlined"
                        component="label"
                        size="small"
                        disabled={uploadingLogo}
                        startIcon={uploadingLogo ? <CircularProgress size={16} /> : <ImageIcon />}
                      >
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                        <input type="file" hidden accept="image/*" onChange={handleLogoUpload} />
                      </Button>
                    )}
                  </Box>
                  {canEdit && (
                    <Typography variant="caption" color="text.secondary">
                      Recommended: PNG or JPG, max 2MB. Uploading saves immediately.
                    </Typography>
                  )}
                </Grid>

                {/* Title Style */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Title Style</Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {STYLE_OPTIONS.map(({ key, label, description }) => {
                      const style = getTenantTitleSx(key, { baseFontSize: 1 });
                      const isSelected = themeForm.titleStyle === key;
                      return (
                        <Paper
                          key={key}
                          onClick={canEdit ? () => handleThemeChange('titleStyle', key) : undefined}
                          sx={{
                            flex: '1 1 120px',
                            p: 1.5,
                            cursor: canEdit ? 'pointer' : 'default',
                            border: isSelected ? '2px solid' : '1px solid',
                            borderColor: isSelected ? 'primary.main' : 'divider',
                            borderRadius: 2,
                            textAlign: 'center',
                            bgcolor: isSelected ? 'action.selected' : 'background.paper',
                            transition: 'all 0.15s',
                            '&:hover': canEdit ? { borderColor: 'primary.main' } : undefined,
                          }}
                        >
                          <Typography
                            sx={{
                              ...style,
                              color: 'text.primary',
                              lineHeight: 1.3,
                              mb: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {tenantName || 'Company'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                            {label}
                          </Typography>
                          <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                            {description}
                          </Typography>
                        </Paper>
                      );
                    })}
                  </Box>
                </Grid>

                {canEdit && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Button variant="contained" onClick={handleSaveTheme} disabled={saving}
                        startIcon={saving ? <CircularProgress size={16} /> : <CheckIcon />}>Save Branding</Button>
                      <Button variant="contained" color="warning"
                        onClick={handleResetTheme} disabled={saving}>
                        Reset to Default
                      </Button>
                      <Typography variant="caption" color="text.secondary">
                        Reset restores the default colour and title style. Your logo is kept, and nothing
                        is applied until you save.
                      </Typography>
                    </Box>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Light-theme preview */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Preview</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Light mode only — dark mode uses the platform's fixed palette.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {/* A — the generated family, read-only */}
                <Grid item xs={12}>
                  <BrandPaletteRow
                    brand={brand}
                    brandSoft={brandSoft}
                    brandHover={brandHover}
                    brandInk={brandInk}
                    brandOn={brandOn}
                  />
                </Grid>

                {/* B — compact application shell */}
                <Grid item xs={12} md={7}>
                  <PreviewLabel>Your application</PreviewLabel>
                  <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', display: 'flex' }}>
                    {/* sidebar — the strongest continuous run of the Brand Color */}
                    <Box sx={{ width: 76, flexShrink: 0, bgcolor: brand, color: brandOn, py: 1.25 }}>
                      <Box sx={{ px: 1, py: 0.55, mx: 0.6, borderRadius: 1, bgcolor: withAlpha(brandOn, 0.18), fontSize: '0.66rem', fontWeight: 600 }}>
                        Dashboard
                      </Box>
                      <Box sx={{ px: 1, py: 0.55, mx: 0.6, mt: 0.3, borderRadius: 1, fontSize: '0.66rem', opacity: 0.8 }}>
                        Invoices
                      </Box>
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* neutral top bar — identity lives in the sidebar, not here */}
                      <Box sx={{
                        display: 'flex', alignItems: 'center', gap: 0.75,
                        px: 1.5, py: 0.9, borderBottom: '1px solid', borderColor: 'divider',
                        bgcolor: 'background.paper'
                      }}>
                        <PaletteIcon sx={{ fontSize: 15, color: brandInk }} />
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                          {tenantName || 'Your Company'}
                        </Typography>
                      </Box>

                      <Box sx={{ p: 1.5 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: brandInk, mb: 1.25 }}>
                          Invoices
                        </Typography>

                        {/* primary table header + one row */}
                        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden', mb: 1.25 }}>
                          <Box sx={{ display: 'flex', bgcolor: brand, color: brandOn, px: 1, py: 0.55 }}>
                            <Typography sx={{ flex: 1, fontSize: '0.62rem', fontWeight: 700 }}>Invoice</Typography>
                            <Typography sx={{ fontSize: '0.62rem', fontWeight: 700 }}>Status</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, bgcolor: 'background.paper' }}>
                            <Typography sx={{ flex: 1, fontSize: '0.65rem' }}>INV-1042</Typography>
                            <Chip label="Paid" size="small" color="success"
                              sx={{ height: 16, fontSize: '0.55rem', '& .MuiChip-label': { px: 0.6 } }} />
                          </Box>
                        </Box>

                        {/* soft branded panel */}
                        <Box sx={{ px: 1.25, py: 0.8, borderRadius: 1, bgcolor: brandSoft }}>
                          <Typography sx={{ fontSize: '0.66rem', color: 'text.secondary' }}>
                            Soft branded panel
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* C — controls, and D — semantic colours */}
                <Grid item xs={12} md={5}>
                  <PreviewLabel>Controls</PreviewLabel>
                  <Paper variant="outlined" sx={{ borderRadius: 2, p: 1.75, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Box sx={{
                        px: 1.75, py: 0.6, borderRadius: 1, fontSize: '0.72rem', fontWeight: 500,
                        bgcolor: brand, color: brandOn, cursor: 'default',
                        border: brandIsPale ? '1px solid ' + brandInk : '1px solid transparent',
                        transition: 'background-color 0.15s',
                        '&:hover': { bgcolor: brandHover }
                      }}>
                        Save
                      </Box>
                      <Box sx={{
                        px: 1.75, py: 0.6, borderRadius: 1, fontSize: '0.72rem', fontWeight: 500,
                        border: '1px solid', borderColor: brandInk, color: brandInk
                      }}>
                        Cancel
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box sx={{ pb: 0.5, borderBottom: '2px solid', borderColor: brandInk, mb: '-1px' }}>
                        <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: brandInk }}>All</Typography>
                      </Box>
                      <Box sx={{ pb: 0.5 }}>
                        <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Draft</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ px: 1, py: 0.7, borderRadius: 1, border: '2px solid', borderColor: brandInk }}>
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Focused input</Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.6, fontSize: '0.62rem' }}>
                        Status colours never change with your brand
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.6, flexWrap: 'wrap' }}>
                        {['success', 'warning', 'error', 'info'].map((tone) => (
                          <Chip key={tone} size="small" color={tone}
                            label={tone.charAt(0).toUpperCase() + tone.slice(1)}
                            sx={{ height: 18, fontSize: '0.58rem' }} />
                        ))}
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* E — login identity, the one full-bleed gradient surface */}
                <Grid item xs={12}>
                  <PreviewLabel>Login screen</PreviewLabel>
                  <Paper sx={{
                    py: 2, px: 2.5,
                    background: 'linear-gradient(120deg, ' + adjustColor(brand, 15) + ' 0%, ' + brand + ' 55%, ' + adjustColor(brand, -30) + ' 100%)',
                    color: brandOn, borderRadius: 2,
                    display: 'flex', alignItems: 'center', gap: 2
                  }}>
                    <img src={resolveImageUrl(themeForm.logoUrl) || '/thaklis-logo.png'} alt="Logo" style={{ maxHeight: 32, objectFit: 'contain' }} />
                    <Box>
                      <Typography sx={{ ...getTenantTitleSx(themeForm.titleStyle), fontSize: '1rem', color: brandOn, lineHeight: 1.2 }}>
                        {tenantName || 'Your Company'}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7, letterSpacing: '0.15em', textTransform: 'uppercase', fontSize: '0.55rem' }}>
                        Management Portal
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Subdomain</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Subdomain" value={newSubdomain} sx={{ mt: 2 }}
            onChange={(e) => { setNewSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); setSubdomainAvailable(null); }}
            onBlur={handleCheckSubdomain}
            InputProps={{ endAdornment: <InputAdornment position="end">
              {checkingSubdomain && <CircularProgress size={20} />}
              {subdomainAvailable === true && <CheckIcon color="success" />}
              {subdomainAvailable === false && <Typography color="error">Taken</Typography>}
              <Typography color="text.secondary" sx={{ ml: 1 }}>.{process.env.REACT_APP_BASE_DOMAIN}</Typography>
            </InputAdornment> }}
            helperText={subdomainAvailable === false ? 'Already in use' : 'Lowercase alphanumeric and hyphens'}
            error={subdomainAvailable === false} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddDomain} disabled={!newSubdomain || !subdomainAvailable || saving}>
            {saving ? <CircularProgress size={20} /> : 'Add Domain'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TenantSettings;
